/**
 * Invoices Page - Professionelle Rechnungs-Verwaltung mit Material-UI
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Stack,
  Avatar,
  Container,
  Paper,
  InputAdornment,
  Switch,
  FormControlLabel,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  Clear as ClearIcon,
  QrCode as QrCodeIcon,
  Close as CloseIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import {
  Invoice,
  Customer,
  Company,
  Rental,
  InvoiceStatus,
} from "../types/api-types";
import { apiClient } from "../services/apiClient";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import { QRCodeSVG } from "qrcode.react";
import {
  buildAuthenticatedPdfUrl,
  buildPublicPdfUrl,
} from "../utils/environment";

const InvoicesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");

  // QR-Code Modal State
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrInvoice, setQrInvoice] = useState<Invoice | null>(null);

  // PDF Modal State
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfInvoice, setPdfInvoice] = useState<Invoice | null>(null);

  // Load data
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [invoiceResponse, customerList, companyList, rentalList] =
        await Promise.all([
          apiClient.getInvoices().catch(() => []),
          apiClient.getCustomers().catch(() => []),
          apiClient.getCompanies().catch(() => []),
          apiClient.getRentals().catch(() => []),
        ]);

      // Die API Response kann ein Array oder ein Objekt mit invoices Property sein
      let invoiceList: Invoice[] = [];
      if (Array.isArray(invoiceResponse)) {
        invoiceList = invoiceResponse;
      } else if (
        invoiceResponse &&
        typeof invoiceResponse === "object" &&
        "invoices" in invoiceResponse
      ) {
        invoiceList = (invoiceResponse as any).invoices || [];
      }

      console.log("Invoice Response:", invoiceResponse);
      console.log("Processed invoices:", invoiceList);

      setInvoices(invoiceList);
      setCustomers(Array.isArray(customerList) ? customerList : []);

      // Extrahiere die eine Firma aus der Company-Liste
      const companyData = Array.isArray(companyList) ? companyList[0] : null;
      setCompany(companyData);

      setRentals(Array.isArray(rentalList) ? rentalList : []);
    } catch (err: any) {
      console.error("Fehler beim Laden der Daten:", err);
      setError(err.message || "Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter invoices
  const filteredInvoices = Array.isArray(invoices)
    ? invoices.filter((invoice) => {
        const customer = customers.find((c) => c.id === invoice.customerId);

        const matchesSearch =
          invoice.invoiceNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          customer?.firstName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          customer?.lastName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          customer?.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company?.name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !statusFilter || invoice.status === statusFilter;

        return matchesSearch && matchesStatus;
      })
    : [];

  // Handle QR Code display
  const handleShowQrCode = (invoice: Invoice) => {
    if (!invoice.qrCode) {
      setError("QR-Code für diese Rechnung nicht verfügbar");
      return;
    }
    setQrInvoice(invoice);
    setShowQrModal(true);
  };

  // Handle PDF download
  const handleDownloadPdf = (invoice: Invoice) => {
    if (!invoice.qrCode) {
      setError("PDF für diese Rechnung nicht verfügbar");
      return;
    }

    setPdfInvoice(invoice);
    setShowPdfModal(true);
  };

  // Handle edit invoice
  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowForm(true);
  };

  // Handle add new invoice
  const handleAddInvoice = () => {
    setSelectedInvoice(null);
    setShowForm(true);
  };

  // Handle status change
  const handleStatusChange = async (
    invoice: Invoice,
    newStatus: InvoiceStatus
  ) => {
    try {
      await apiClient.updateInvoiceStatus(invoice.id, { status: newStatus });
      await loadData();
    } catch (err: any) {
      setError(err.message || "Fehler beim Aktualisieren des Status");
    }
  };

  // Handle delete invoice (only for drafts)
  const handleDeleteInvoice = async (invoice: Invoice) => {
    if (invoice.status !== InvoiceStatus.DRAFT) {
      setError("Nur Entwürfe können gelöscht werden");
      return;
    }

    if (
      !window.confirm(
        `Möchten Sie den Entwurf "${invoice.invoiceNumber}" wirklich löschen?`
      )
    ) {
      return;
    }

    try {
      await apiClient.deleteInvoice(invoice.id);
      await loadData();
    } catch (err: any) {
      setError(err.message || "Fehler beim Löschen der Rechnung");
    }
  };

  // Status display with enhanced styling
  const getStatusChip = (status: string) => {
    const statusConfig: Record<
      string,
      {
        label: string;
        color:
          | "default"
          | "primary"
          | "secondary"
          | "error"
          | "info"
          | "success"
          | "warning";
        icon?: React.ReactElement;
      }
    > = {
      [InvoiceStatus.DRAFT]: {
        label: "Entwurf",
        color: "info",
        icon: <EditIcon sx={{ fontSize: 16 }} />,
      },
      [InvoiceStatus.PAID]: {
        label: "Bezahlt",
        color: "success",
        icon: <CheckIcon sx={{ fontSize: 16 }} />,
      },
    };

    const config = statusConfig[status] || {
      label: status,
      color: "default" as const,
    };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        {...(config.icon && { icon: config.icon })}
        sx={{
          fontWeight: "bold",
          borderRadius: 2,
          "& .MuiChip-icon": {
            marginLeft: "4px",
          },
        }}
      />
    );
  };

  // Format date
  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
  };

  if (loading) {
    return <LoadingSpinner text="Lade Rechnungen..." />;
  }

  if (showForm) {
    return (
      <InvoiceForm
        invoice={selectedInvoice}
        customers={customers}
        company={company}
        rentals={rentals}
        onSave={async (invoiceData) => {
          try {
            if (selectedInvoice) {
              // Bestehende Rechnung aktualisieren
              await apiClient.updateInvoice(selectedInvoice.id, invoiceData);
            } else {
              // Neue Rechnung erstellen
              await apiClient.createInvoice(invoiceData);
            }
            setShowForm(false);
            setSelectedInvoice(null);
            await loadData();
          } catch (err: any) {
            setError(err.message || "Fehler beim Speichern der Rechnung");
          }
        }}
        onCancel={() => {
          setShowForm(false);
          setSelectedInvoice(null);
        }}
      />
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <ReceiptIcon fontSize={isMobile ? "medium" : "large"} />
              Rechnungsverwaltung
            </Typography>
          </Box>

          {!isMobile && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleAddInvoice}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.3)",
                  },
                  fontWeight: "bold",
                }}
              >
                Neue Rechnung
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            Filter & Suche
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Rechnungen durchsuchen"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchTerm("")}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Rechnungsnummer, Kunde, Firma suchen..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">Alle Status</MenuItem>
                  <MenuItem value={InvoiceStatus.DRAFT}>Entwurf</MenuItem>
                  <MenuItem value={InvoiceStatus.PAID}>Bezahlt</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="flex-end"
              >
                {(searchTerm || statusFilter) && (
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    sx={{ borderRadius: 2 }}
                  >
                    Filter zurücksetzen
                  </Button>
                )}
                {isMobile && (
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={handleAddInvoice}
                    sx={{ borderRadius: 2 }}
                  >
                    Neue Rechnung
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Invoices Display */}
      {filteredInvoices.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: "center",
            borderRadius: 2,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <ReceiptIcon
            sx={{
              fontSize: { xs: 60, md: 80 },
              color: "text.secondary",
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? "h6" : "h5"}
            gutterBottom
            fontWeight="bold"
          >
            Keine Rechnungen gefunden
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {searchTerm || statusFilter
              ? "Passen Sie Ihre Suchkriterien an oder erstellen Sie eine neue Rechnung."
              : "Starten Sie mit Ihrer ersten Rechnung und verwalten Sie alle Kundenrechnungen professionell."}
          </Typography>
          {!searchTerm && !statusFilter && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleAddInvoice}
              sx={{
                mt: 3,
                borderRadius: 2,
                px: 4,
                py: 1.5,
              }}
            >
              Erste Rechnung erstellen
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredInvoices.map((invoice) => {
            const customer = customers.find((c) => c.id === invoice.customerId);

            return (
              <Grid item xs={12} md={6} lg={4} key={invoice.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Header with Status */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{
                            fontWeight: "bold",
                            color: "primary.main",
                            mb: 0.5,
                          }}
                        >
                          {invoice.invoiceNumber}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          Erstellt: {formatDate(invoice.issueDate)}
                        </Typography>
                      </Box>
                      {getStatusChip(invoice.status)}
                    </Box>

                    {/* Customer Info */}
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 2.5 }}
                    >
                      <Avatar
                        sx={{
                          mr: 2,
                          bgcolor: "primary.main",
                          width: 40,
                          height: 40,
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{
                            fontSize: "0.9rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {customer
                            ? `${customer.firstName} ${customer.lastName}`
                            : "Barkauf"}
                        </Typography>
                        {customer?.company && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.75rem",
                              display: "block",
                              mt: 0.5,
                            }}
                          >
                            {customer.company}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Amount Info */}
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <Typography
                        variant="h5"
                        color="primary.main"
                        fontWeight="bold"
                        sx={{ mb: 1 }}
                      >
                        {formatCurrency(Number(invoice.totalAmount) || 0)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Gesamtbetrag
                      </Typography>
                    </Box>

                    {/* Notes */}
                    {invoice.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.75rem",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.4,
                          }}
                        >
                          {invoice.notes}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ width: "100%" }}
                      justifyContent="space-between"
                    >
                      {/* Action Buttons */}
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Bearbeiten">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditInvoice(invoice)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {invoice.status === InvoiceStatus.DRAFT && (
                          <>
                            <Tooltip title="Als bezahlt markieren">
                              <IconButton
                                color="success"
                                onClick={() =>
                                  handleStatusChange(
                                    invoice,
                                    InvoiceStatus.PAID
                                  )
                                }
                                size="small"
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Löschen">
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteInvoice(invoice)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>

                      {/* PDF & QR Actions */}
                      <Stack direction="row" spacing={0.5}>
                        {invoice.qrCode && (
                          <>
                            <Tooltip title="QR-Code anzeigen">
                              <IconButton
                                color="primary"
                                onClick={() => handleShowQrCode(invoice)}
                                size="small"
                              >
                                <QrCodeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="PDF öffnen">
                              <IconButton
                                color="secondary"
                                onClick={() => handleDownloadPdf(invoice)}
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          display: { xs: "flex", md: "none" },
        }}
        onClick={handleAddInvoice}
      >
        <AddIcon />
      </Fab>

      {/* QR-Code Modal - Optimiert für alle Geräte */}
      <Dialog
        open={showQrModal}
        onClose={() => setShowQrModal(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: "100vh", sm: "calc(100vh - 64px)" },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "white",
            fontWeight: "bold",
            pb: { xs: 2, sm: 2 },
            pt: { xs: 3, sm: 3 },
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="span"
            sx={{
              color: "white",
              fontWeight: "bold",
            }}
          >
            QR-Code für Rechnung
          </Typography>
          <IconButton
            onClick={() => setShowQrModal(false)}
            sx={{
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", py: { xs: 3, sm: 4 } }}>
          {qrInvoice && (
            <Box>
              <Typography
                variant={isMobile ? "body1" : "h6"}
                gutterBottom
                sx={{
                  color: "white",
                  mb: { xs: 3, sm: 4 },
                  fontWeight: "medium",
                }}
              >
                {qrInvoice.invoiceNumber}
              </Typography>

              {/* QR-Code Container */}
              <Paper
                sx={{
                  p: { xs: 3, sm: 4 },
                  display: "inline-block",
                  borderRadius: 3,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
                  bgcolor: "white",
                }}
              >
                <QRCodeSVG
                  value={buildPublicPdfUrl(qrInvoice.qrCode!)}
                  size={isMobile ? 200 : 240}
                  level="M"
                  includeMargin={true}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </Paper>

              <Typography
                variant="body2"
                sx={{
                  mt: { xs: 3, sm: 4 },
                  color: "rgba(255,255,255,0.9)",
                  maxWidth: "85%",
                  mx: "auto",
                  lineHeight: 1.6,
                  fontSize: { xs: "0.875rem", sm: "0.9rem" },
                }}
              >
                Scannen Sie den QR-Code mit der Kamera oder teilen Sie den Link,
                um die Rechnung öffentlich anzuzeigen
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            pb: { xs: 3, sm: 4 },
            px: { xs: 3, sm: 4 },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 1 },
          }}
        >
          <Button
            onClick={() => {
              if (qrInvoice?.qrCode) {
                const url = buildPublicPdfUrl(qrInvoice.qrCode);
                if (navigator.share) {
                  navigator.share({
                    title: `Rechnung ${qrInvoice.invoiceNumber}`,
                    text: `Rechnung ${qrInvoice.invoiceNumber} ansehen`,
                    url: url,
                  });
                } else {
                  navigator.clipboard.writeText(url);
                  // Hier könnte man eine Erfolgs-Nachricht anzeigen
                }
              }
            }}
            variant="contained"
            size="large"
            startIcon={<ShareIcon />}
            fullWidth={isMobile}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              borderRadius: 2,
              py: 1.5,
              fontWeight: "bold",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.3)",
              },
              mr: { xs: 0, sm: 2 },
            }}
          >
            Link teilen
          </Button>

          <Button
            onClick={() => {
              if (qrInvoice?.qrCode) {
                handleDownloadPdf(qrInvoice);
              }
            }}
            variant="contained"
            size="large"
            startIcon={<DownloadIcon />}
            fullWidth={isMobile}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              borderRadius: 2,
              py: 1.5,
              fontWeight: "bold",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.3)",
              },
            }}
          >
            PDF anzeigen
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Modal - Optimiert für alle Geräte */}
      <Dialog
        open={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: "100vh", sm: "calc(100vh - 64px)" },
            height: { xs: "100vh", sm: "80vh" },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: "bold",
            pb: { xs: 1, sm: 2 },
            pt: { xs: 2, sm: 3 },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="span"
            sx={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <VisibilityIcon />
            PDF Vorschau - {pdfInvoice?.invoiceNumber}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="PDF herunterladen">
              <IconButton
                onClick={() => {
                  if (pdfInvoice?.qrCode) {
                    const pdfUrl = buildAuthenticatedPdfUrl(pdfInvoice.qrCode);
                    const link = document.createElement("a");
                    link.href = pdfUrl;
                    link.download = `Rechnung-${pdfInvoice.invoiceNumber}.pdf`;
                    link.click();
                  }
                }}
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => setShowPdfModal(false)}
              sx={{
                bgcolor: "grey.100",
                "&:hover": {
                  bgcolor: "grey.200",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {pdfInvoice?.qrCode && (
            <Box
              sx={{
                flex: 1,
                width: "100%",
                height: "100%",
                minHeight: { xs: "calc(100vh - 140px)", sm: "500px" },
                position: "relative",
              }}
            >
              <iframe
                src={buildAuthenticatedPdfUrl(pdfInvoice.qrCode)}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: isMobile ? "0" : "8px",
                }}
                title={`PDF Vorschau - ${pdfInvoice.invoiceNumber}`}
                allowFullScreen
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

// Invoice Form Component
interface InvoiceFormProps {
  invoice: Invoice | null;
  customers: Customer[];
  company: Company | null;
  rentals: Rental[];
  onSave: (invoice: any) => void;
  onCancel: () => void;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoice,
  customers,
  company,
  rentals,
  onSave,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const [formData, setFormData] = useState({
    customerId: invoice?.customerId || "",
    companyId: company?.id || "",
    rentalId: invoice?.rentalId || "",
    status: invoice?.status || InvoiceStatus.DRAFT,
    issueDate: invoice?.issueDate
      ? new Date(invoice.issueDate).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    dueDate: invoice?.dueDate
      ? new Date(invoice.dueDate).toISOString().slice(0, 10)
      : "",
    notes: invoice?.notes || "",
  });

  const [items, setItems] = useState(
    invoice?.items || [
      {
        description: "",
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0,
        unit: "Stück",
        category: "CONSUMABLE",
        articleId: undefined,
      },
    ]
  );

  // Berechnung der Steuerbeträge basierend auf Bruttopreisen
  const [calculatedTotals, setCalculatedTotals] = useState({
    grossTotal: 0,
    netTotal: 0,
    taxAmount: 0,
    taxRate: 0,
  });

  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  // Calculate totals based on company tax settings
  useEffect(() => {
    if (!company) return;

    const grossTotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
    const isSmallBusiness = company.isSmallBusiness;
    const taxRate = isSmallBusiness ? 0 : company.defaultVatRate || 19;

    let netTotal = grossTotal;
    let taxAmount = 0;

    if (!isSmallBusiness && taxRate > 0) {
      // Bruttopreis → Nettopreis berechnen
      netTotal = grossTotal / (1 + taxRate / 100);
      taxAmount = grossTotal - netTotal;
    }

    setCalculatedTotals({
      grossTotal,
      netTotal,
      taxAmount,
      taxRate,
    });
  }, [items, company]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!company) {
      alert("Firmendaten sind nicht verfügbar.");
      return;
    }

    const invoiceData = {
      customerId: formData.customerId || undefined,
      subtotal: calculatedTotals.grossTotal, // Bruttogesamtbetrag
      items: items.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice, // Bruttopreis
        totalPrice: item.totalPrice, // Bruttogesamtpreis
        unit: item.unit,
        category: item.category,
        articleId: item.articleId,
      })),
      notes: formData.notes || undefined,
      // Steuerinformationen werden im Backend aus der Company geladen
      // rentalId: formData.rentalId // Falls später für Mieten benötigt
    };

    onSave(invoiceData);
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Spezielle Logik für SERVICE-Kategorie
    if (field === "category" && value === "SERVICE") {
      newItems[index].quantity = 1;
      newItems[index].unit = "Pauschal";
    }

    // Recalculate item total price
    if (field === "quantity" || field === "unitPrice") {
      newItems[index].totalPrice =
        newItems[index].quantity * newItems[index].unitPrice;
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        description: "",
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0,
        unit: "Stück",
        category: "CONSUMABLE",
        articleId: undefined,
      },
    ]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <ReceiptIcon fontSize={isMobile ? "medium" : "large"} />
              {invoice ? "Rechnung bearbeiten" : "Neue Rechnung erstellen"}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Professionelle Rechnungserstellung für Ihre Werkstatt
            </Typography>
          </Box>

          {!isMobile && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="large"
                onClick={onCancel}
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.5)",
                  "&:hover": {
                    borderColor: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Abbrechen
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: "primary.main",
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <BusinessIcon />
            Grundinformationen
          </Typography>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value as InvoiceStatus,
                    }))
                  }
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  {Object.values(InvoiceStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status === InvoiceStatus.DRAFT && "Entwurf"}
                      {status === InvoiceStatus.PAID && "Bezahlt"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Kunde</InputLabel>
                <Select
                  value={formData.customerId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      customerId: e.target.value,
                    }))
                  }
                  label="Kunde"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Barkauf (kein Kunde)</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.firstName} {customer.lastName}
                      {customer.company && ` (${customer.company})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Firma"
                value={company?.name || "Nicht verfügbar"}
                variant="outlined"
                disabled
                sx={{
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                helperText="Firmendaten werden automatisch geladen"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Rechnungsdatum"
                type="date"
                value={formData.issueDate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    issueDate: e.target.value,
                  }))
                }
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Fälligkeitsdatum"
                type="date"
                value={formData.dueDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, dueDate: e.target.value }))
                }
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Tax Information Section */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: "primary.main",
              mb: 2,
            }}
          >
            Steuereinstellungen
          </Typography>

          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              bgcolor: "grey.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: "medium" }}
            >
              <strong>Steuerstatus:</strong>{" "}
              {company?.isSmallBusiness
                ? "Kleinunternehmerregelung (§19 UStG) - Keine Umsatzsteuer"
                : `Regelbesteuerung - ${company?.defaultVatRate || 19}% MwSt.`}
            </Typography>
            {company?.isSmallBusiness ? (
              <Typography variant="body2" color="text.secondary">
                Alle Preise sind Endpreise. Es wird keine Umsatzsteuer
                ausgewiesen.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Alle eingegebenen Preise sind Bruttopreise inkl. MwSt. Die
                Steuer wird automatisch berechnet.
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* Invoice Items Section */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ReceiptIcon />
              Rechnungspositionen
            </Typography>
            <Button
              variant="outlined"
              onClick={addItem}
              startIcon={<AddIcon />}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Position hinzufügen
            </Button>
          </Stack>

          <Stack spacing={2}>
            {items.map((item, index) => (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  "&:hover": {
                    boxShadow: 2,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Position {index + 1}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                      startIcon={<DeleteIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      Entfernen
                    </Button>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Beschreibung"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                        variant="outlined"
                        multiline={isMobile}
                        rows={isMobile ? 2 : 1}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <TextField
                        select
                        fullWidth
                        label="Kategorie"
                        value={item.category || "CONSUMABLE"}
                        onChange={(e) =>
                          handleItemChange(index, "category", e.target.value)
                        }
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      >
                        <MenuItem value="TOOL">🔧 Werkzeug</MenuItem>
                        <MenuItem value="LUBRICANT">🛢️ Schmierstoffe</MenuItem>
                        <MenuItem value="CONSUMABLE">
                          📦 Verbrauchsmaterial
                        </MenuItem>
                        <MenuItem value="SERVICE">⚙️ Dienstleistung</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Menge"
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "quantity",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        variant="outlined"
                        disabled={item.category === "SERVICE"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Einheit"
                        value={item.unit || "Stück"}
                        onChange={(e) =>
                          handleItemChange(index, "unit", e.target.value)
                        }
                        variant="outlined"
                        disabled={item.category === "SERVICE"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label={
                          company?.isSmallBusiness
                            ? "Einzelpreis"
                            : "Einzelpreis (brutto)"
                        }
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "unitPrice",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EuroIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          !company?.isSmallBusiness ? "inkl. MwSt." : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={
                          company?.isSmallBusiness
                            ? "Gesamtpreis"
                            : "Gesamtpreis (brutto)"
                        }
                        value={item.totalPrice.toFixed(2)}
                        variant="outlined"
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EuroIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          !company?.isSmallBusiness ? "inkl. MwSt." : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Paper>

        {/* Summary Section */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  color: "primary.main",
                  mb: 2,
                }}
              >
                Zusätzliche Informationen
              </Typography>
              <TextField
                fullWidth
                label="Notizen & Bemerkungen"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                variant="outlined"
                placeholder="Zusätzliche Informationen zur Rechnung..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  mb: 3,
                }}
              >
                Rechnungssumme
              </Typography>

              {company?.isSmallBusiness ? (
                // Kleinunternehmer: Nur Gesamtbetrag
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h4"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    {formatCurrency(calculatedTotals.grossTotal)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Gesamtbetrag (ohne MwSt.)
                  </Typography>
                  <Chip
                    label="Kleinunternehmer (§19 UStG)"
                    size="small"
                    variant="outlined"
                    sx={{ mt: 2 }}
                  />
                </Box>
              ) : (
                // Regelbesteuerung: Aufschlüsselung
                <Stack spacing={2}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body1">Nettobetrag:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(calculatedTotals.netTotal)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body1">
                      MwSt ({calculatedTotals.taxRate}%):
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(calculatedTotals.taxAmount)}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Gesamtbetrag (brutto):
                    </Typography>
                    <Typography
                      variant="h6"
                      color="primary.main"
                      fontWeight="bold"
                    >
                      {formatCurrency(calculatedTotals.grossTotal)}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mt: 3, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              onClick={onCancel}
              size="large"
              sx={{
                borderRadius: 2,
                order: { xs: 2, sm: 1 },
              }}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<CheckIcon />}
              sx={{
                borderRadius: 2,
                px: 4,
                order: { xs: 1, sm: 2 },
              }}
            >
              {invoice ? "Rechnung aktualisieren" : "Rechnung erstellen"}
            </Button>
          </Stack>
        </Paper>
      </form>
    </Container>
  );
};

export default InvoicesPage;
